/**
 * TextField component - text/number input with validation support.
 * @module components/ui/Form/TextField
 */

import {
  type ChangeEvent,
  type HTMLInputTypeAttribute,
  type KeyboardEventHandler,
  type Ref,
  useId,
} from "react";

import { Button, Input, Join } from "react-daisyui";

import { useEditLock, useLockedRefusal } from "../EditLock";
import type { FieldSize } from "./field-sizes";
import { ReadOnlyValue } from "./ReadOnlyValue";

export interface TextFieldProps {
  /** Unique ID for the input */
  id: string;
  /** Current value */
  value: string | number;
  /** Change handler */
  onChange: (value: string) => void;
  /** Called when focus leaves, for a field that normalises what was typed. */
  onBlur?: () => void;
  /** For a field with keys of its own, such as Enter to commit and Esc to cancel. */
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  /** The input element, for a caller that moves focus between fields. */
  inputRef?: Ref<HTMLInputElement>;
  /** Input type */
  type?: HTMLInputTypeAttribute;
  /** Placeholder text */
  placeholder?: string;
  /**
   * Refuses edits but stays focusable, as `aria-disabled` plus `readOnly`:
   * a natively disabled field leaves the tab order and takes its reason with it.
   */
  disabled?: boolean;
  /** Why it is disabled: a tooltip on hover and focus, and its description. */
  disabledReason?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the field is read-only */
  readOnly?: boolean;
  /** Keyboard a touch screen raises, such as `decimal` for hours. */
  inputMode?: "decimal" | "numeric" | "text";
  /** Minimum value (for number inputs) */
  min?: number;
  /** Maximum value (for number inputs) */
  max?: number;
  /** Step value (for number inputs) */
  step?: number;
  /** Size variant */
  size?: FieldSize;
  /** Suffix text (e.g., "credits") */
  suffix?: string;
  /** Prefix text */
  prefix?: string;
  /** Additional CSS classes */
  className?: string;
  /** Test ID */
  "data-testid"?: string;
  /** Aria describedby ID */
  "aria-describedby"?: string;
  /** Aria label */
  "aria-label"?: string;
  /** Set by `FormField` while it holds an error for this control. */
  "aria-invalid"?: boolean;
}

/**
 * TextField component for text and number inputs.
 */
export function TextField({
  id,
  value,
  onChange,
  onBlur = undefined,
  onKeyDown = undefined,
  inputRef = undefined,
  type = "text",
  placeholder = undefined,
  disabled: ownDisabled = false,
  required = false,
  readOnly = false,
  inputMode = undefined,
  min = undefined,
  max = undefined,
  step = undefined,
  size = undefined,
  suffix = undefined,
  prefix = undefined,
  className = "",
  "data-testid": testId,
  "aria-describedby": ariaDescribedby,
  "aria-label": ariaLabel,
  "aria-invalid": ariaInvalid = undefined,
  ...reasonProps
}: TextFieldProps) {
  // Left in the rest so its presence is readable: a caller that can refuse
  // with a reason keeps one wrapper throughout, so the control is not
  // remounted, and does not lose focus, as the reason comes and goes.
  const { disabled, offersReason, disabledReason } = useLockedRefusal(ownDisabled, reasonProps);
  const reasonId = useId();
  const { locked } = useEditLock();
  const reason = disabled && disabledReason ? disabledReason : undefined;
  const describedBy =
    [ariaDescribedby, reason ? reasonId : undefined].filter(Boolean).join(" ") || undefined;
  if (locked) {
    return (
      <ReadOnlyValue
        id={id}
        value={
          String(value) === "" ? "" : `${prefix ?? ""}${String(value)}${suffix ? ` ${suffix}` : ""}`
        }
        data-testid={testId}
        aria-describedby={ariaDescribedby}
        aria-label={ariaLabel}
      />
    );
  }
  // daisyUI paints a refused field from `:disabled` only.
  const refused = disabled ? "cursor-not-allowed border-base-200 bg-base-200 text-muted" : "";

  // The input is the join item itself: a wrapper there takes the join's
  // border and reads as focused at rest.
  const joined = Boolean(suffix) || Boolean(prefix);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (disabled) {
      return;
    }
    onChange(e.target.value);
  };

  const input = (
    <Input
      id={id}
      type={type}
      value={value}
      onChange={handleChange}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      ref={inputRef}
      placeholder={placeholder}
      aria-disabled={disabled || undefined}
      required={required}
      readOnly={readOnly || disabled}
      inputMode={inputMode}
      min={min}
      max={max}
      step={step}
      size={size}
      className={[
        joined ? "join-item min-w-0 flex-1" : "w-full",
        "text-sm",
        refused,
        joined ? "" : className,
      ]
        .filter(Boolean)
        .join(" ")}
      data-testid={testId}
      aria-describedby={describedBy}
      aria-label={ariaLabel}
      aria-invalid={ariaInvalid}
      aria-required={required}
    />
  );

  const field = joined ? (
    <Join className={`w-full${className ? ` ${className}` : ""}`}>
      {prefix && (
        <Button
          tag="span"
          color="ghost"
          animation={false}
          className="join-item pointer-events-none"
        >
          {prefix}
        </Button>
      )}
      {input}
      {suffix && (
        <Button
          tag="span"
          color="ghost"
          animation={false}
          className="join-item pointer-events-none"
        >
          {suffix}
        </Button>
      )}
    </Join>
  ) : (
    input
  );

  if (!offersReason) {
    return field;
  }
  return (
    <div className={reason ? "tooltip block w-full" : "contents"} data-tip={reason}>
      {field}
      {reason ? (
        <span id={reasonId} className="sr-only">
          {reason}
        </span>
      ) : null}
    </div>
  );
}

export default TextField;
